import { LEGAL_NOTICE } from '@/config/env'
import { getMobiwayCountry } from '@/lib/axios'
import { AvailableCountry } from '@/types/openapi'

// Languages supported by the app (kept in sync with i18next's supportedLngs)
export const LEGAL_LANGS = ['fr', 'en', 'es'] as const
export type LegalLang = (typeof LEGAL_LANGS)[number]

type LegalCountryConfig = {
  /** Language used when no `lang` is provided in the URL, or when the requested one isn't available for this country. */
  defaultLang: LegalLang
  /** Languages for which a translated CGU file actually exists for this country. */
  availableLangs: LegalLang[]
}

// Add a language to `availableLangs` only once its translated static file exists in `public/static/legal/{country}`.
export const LEGAL_CONFIG: Record<AvailableCountry, LegalCountryConfig> = {
  [AvailableCountry.FR]: { defaultLang: 'fr', availableLangs: ['fr', 'en', 'es'] },
  [AvailableCountry.CO]: { defaultLang: 'es', availableLangs: ['es', 'fr', 'en'] },
}

/**
 * Resolves the effective CGU language for a given country:
 * - Uses the requested `lang` if it's one of the country's available translations.
 * - Otherwise falls back to the country's default language.
 */
export function resolveCguLang(country: AvailableCountry, lang?: string): LegalLang {
  const { defaultLang, availableLangs } = LEGAL_CONFIG[country]
  return lang && availableLangs.includes(lang as LegalLang) ? (lang as LegalLang) : defaultLang
}

/** Builds the static asset URL of the CGU document for a given country and language. */
export function getCguUrl(country: AvailableCountry, lang: LegalLang): string {
  return `/static/legal/${country.toLowerCase()}/${lang}.html`
}

// The Nexans corporate site (www.nexans.{country}) doesn't have the same language pages available as our own
// CGU: the .co site only has es/en (no fr), and the .fr site only has fr/en (no es). Requesting an unavailable
// language returns a 404 on their side, so we must fall back to the country's default language ourselves.
const LEGAL_NOTICE_CONFIG: Record<AvailableCountry, LegalCountryConfig> = {
  [AvailableCountry.FR]: { defaultLang: 'fr', availableLangs: ['fr', 'en'] },
  [AvailableCountry.CO]: { defaultLang: 'es', availableLangs: ['es', 'en'] },
}

/**
 * Resolves the effective language for the Nexans legal notice page of a given country:
 * - Uses the requested `lang` if the Nexans site has a page in that language for this country.
 * - Otherwise falls back to the country's default language on the Nexans site.
 */
function resolveLegalNoticeLang(country: AvailableCountry, lang?: string): LegalLang {
  const { defaultLang, availableLangs } = LEGAL_NOTICE_CONFIG[country]
  return lang && availableLangs.includes(lang as LegalLang) ? (lang as LegalLang) : defaultLang
}

/**
 * Builds the Nexans corporate legal notice URL for the given language, using `VITE_APP_LEGAL_NOTICE` as a
 * template (`{country}` and `{lang}` placeholders), so the legal notice always points to the Nexans site matching this app's own domain.
 *
 * Falls back to `FR` when the country can't be resolved (e.g. local/dev/qal without the override set), and to
 * that country's default language on the Nexans site when the requested language isn't available there.
 */
export function getLegalNoticeUrl(lang: string): string {
  const country = getMobiwayCountry() ?? AvailableCountry.FR
  const effectiveLang = resolveLegalNoticeLang(country, lang)
  return LEGAL_NOTICE.replace('{country}', country.toLowerCase()).replace('{lang}', effectiveLang)
}
